import React, { useState, useEffect } from 'react'
import './NewContactsPage.css'
import { useSelector, useDispatch } from "react-redux"
import MenuBar from "../components/MenuBar"
import Header from "../components/Header"
import { useNavigate } from "react-router-dom"
import LoadingSpinner from '../components/placeholders/LoadingSpinner'
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos'
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos'
import httpClient from '../components/helpers/httpClient'
import DropDownFilter from '../components/DropDownFilter'
import TableSelectCheckbox from '../components/TableSelectCheckbox'
import { updateFilters, updateSubmissionsList, updateSourcesList, updatePagination } from '../store/newContactsPage'
import SearchIcon from '@mui/icons-material/Search'
import ClearIcon from '@mui/icons-material/Clear'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import FiberNewIcon from '@mui/icons-material/FiberNew'
import PersonIcon from '@mui/icons-material/Person'
import VisibilityIcon from '@mui/icons-material/Visibility'
import SendIcon from '@mui/icons-material/Send'
import CloseIcon from '@mui/icons-material/Close'
import RefreshIcon from '@mui/icons-material/Refresh'
import { toast } from "react-toastify"
import { showNotificationPopup } from '../components/NotificationPopup'

function NewContactsPage() {
  const [loadingSubmissions, setLoadingSubmissions] = useState(false)
  const [selectedSubmissions, setSelectedSubmissions] = useState([])
  const [deleteModalOpen, setDeleteModalOpen] = useState(false)
  const [deleteWithContact, setDeleteWithContact] = useState(false)
  const [deletingSubmission, setDeletingSubmission] = useState(null) // For single delete
  const [viewModalOpen, setViewModalOpen] = useState(false)
  const [viewingSubmission, setViewingSubmission] = useState(null)
  const [sendingBrochureIds, setSendingBrochureIds] = useState([])

  const dispatch = useDispatch()
  const navigate = useNavigate()
  const filters = useSelector(state => state.newContactsPage.filters)
  const submissions = useSelector(state => state.newContactsPage.submissionsList)
  const sources = useSelector(state => state.newContactsPage.sourcesList)
  const totalPages = useSelector(state => state.newContactsPage.totalPages)
  const totalCount = useSelector(state => state.newContactsPage.totalCount)

  const limit = 25

  const fetchSubmissions = async () => {
    setLoadingSubmissions(true)
    try {
      const queryParams = new URLSearchParams({
        page: filters.page,
        limit: limit,
        ...(filters.searchWord && { search: filters.searchWord }),
        ...(filters.sourceFilter && { source: filters.sourceFilter }),
        ...(filters.sortFilter && { sort: filters.sortFilter }),
        ascending: filters.sortAscending
      }).toString()

      const response = await httpClient.get(
        import.meta.env.VITE_API_URL + `/api/new-contacts?${queryParams}`
      )
      
      dispatch(updateSubmissionsList(response.data.submissions || []))
      dispatch(updateSourcesList(response.data.sources || []))
      dispatch(updatePagination({
        totalPages: response.data.total_pages || 1,
        totalCount: response.data.total_count || 0,
        page: response.data.page
      }))
    } catch (error) {
      console.error('Error fetching submissions:', error)
      toast.error('Failed to load submissions')
    }
    setLoadingSubmissions(false)
  }

  useEffect(() => {
    fetchSubmissions()
  }, [])

  useEffect(() => {
    fetchSubmissions()
    setSelectedSubmissions([]) // Clear selections when filters change
  }, [filters])

  const handleOnClickRow = (contactId) => {
    navigate(`/contacts/${contactId}`)
  }

  const handlePagination = (event) => {
    const element = event.currentTarget
    if (element.classList.contains("grayed-out")) {
      return
    }
    const type = element.dataset.type
    const updatedFilters = { ...filters }
    switch (type) {
      case "previous":
        updatedFilters["page"] = filters.page - 1
        dispatch(updateFilters(updatedFilters))
        break
      case "next":
        updatedFilters["page"] = filters.page + 1
        dispatch(updateFilters(updatedFilters))
        break
    }
  }

  const handleOnClickDropdownItem = (e, id = null, idList) => {
    let value = e.currentTarget.textContent
    const newFiltersObj = { ...filters }
    switch (idList) {
      case 'new_contacts_filter--source':
        newFiltersObj.sourceFilter = value
        break
    }
    newFiltersObj.page = 1
    dispatch(updateFilters(newFiltersObj))
  }

  const handleSearchWord = (event) => {
    const updatedFilters = { ...filters }
    updatedFilters["searchWord"] = event.target.value
    updatedFilters["page"] = 1
    dispatch(updateFilters(updatedFilters))
  }

  const handleClearSearchBar = () => {
    if (filters.searchWord === "") {
      return
    } else {
      const updatedFilters = { ...filters }
      updatedFilters["searchWord"] = ""
      updatedFilters["page"] = 1
      dispatch(updateFilters(updatedFilters))
    }
  }

  const handleSetSortFilter = (event) => {
    const innerHTML = event.currentTarget.innerHTML
    const updatedFilters = { ...filters }
    let sortField = null

    switch (true) {
      case innerHTML.includes("Email"):
        sortField = "contact_email"
        break
      case innerHTML.includes("Source"):
        sortField = "source_tag"
        break
      case innerHTML.includes("Submitted"):
        sortField = "submitted_at"
        break
    }

    if (sortField) {
      if (filters.sortFilter === sortField) {
        updatedFilters["sortAscending"] = !filters.sortAscending
      } else {
        updatedFilters["sortFilter"] = sortField
        updatedFilters["sortAscending"] = true
      }
      updatedFilters["page"] = 1
      dispatch(updateFilters(updatedFilters))
    }
  }

  const handleSelectSubmission = (submissionId) => {
    setSelectedSubmissions(prev => {
      if (prev.includes(submissionId)) {
        return prev.filter(id => id !== submissionId)
      } else {
        return [...prev, submissionId]
      }
    })
  }

  const handleSelectAll = () => {
    if (selectedSubmissions.length === submissions.length) {
      setSelectedSubmissions([])
    } else {
      setSelectedSubmissions(submissions.map(s => s.id))
    }
  }

  const handleBulkApprove = async () => {
    if (selectedSubmissions.length === 0) {
      toast.warning("Please select at least one submission")
      return
    }

    try {
      await httpClient.post(
        import.meta.env.VITE_API_URL + '/api/new-contacts/bulk-approve',
        { submission_ids: selectedSubmissions }
      )
      toast.success(`Approved ${selectedSubmissions.length} submission(s)`)
      setSelectedSubmissions([])
      fetchSubmissions()
    } catch (error) {
      console.error('Error approving submissions:', error)
      toast.error('Failed to approve submissions')
    }
  }

  const handleSingleApprove = async (e, submissionId) => {
    e.stopPropagation()
    
    try {
      await httpClient.post(
        import.meta.env.VITE_API_URL + `/api/new-contacts/${submissionId}/approve`
      )
      toast.success('Submission approved')
      fetchSubmissions()
    } catch (error) {
      console.error('Error approving submission:', error)
      toast.error('Failed to approve submission')
    }
  }

  const handleBulkDelete = async () => {
    if (selectedSubmissions.length === 0) {
      toast.warning("Please select at least one submission")
      return
    }
    setDeletingSubmission(null) // Bulk mode
    setDeleteModalOpen(true)
  }

  const handleSingleDelete = (e, submission) => {
    e.stopPropagation()
    setDeletingSubmission(submission) // Single mode
    setDeleteModalOpen(true)
  }

  const confirmDelete = async () => {
    try {
      if (deletingSubmission) {
        // Single delete
        await httpClient.post(
          import.meta.env.VITE_API_URL + '/api/new-contacts/bulk-delete',
          { 
            submission_ids: [deletingSubmission.id],
            delete_contacts: deleteWithContact
          }
        )
        toast.success(`Deleted submission${deleteWithContact ? ' with contact' : ''}`)
      } else {
        // Bulk delete
        await httpClient.post(
          import.meta.env.VITE_API_URL + '/api/new-contacts/bulk-delete',
          { 
            submission_ids: selectedSubmissions,
            delete_contacts: deleteWithContact
          }
        )
        toast.success(`Deleted ${selectedSubmissions.length} submission(s)${deleteWithContact ? ' with contacts' : ''}`)
        setSelectedSubmissions([])
      }
      setDeleteModalOpen(false)
      setDeleteWithContact(false)
      setDeletingSubmission(null)
      fetchSubmissions()
    } catch (error) {
      console.error('Error deleting submissions:', error)
      toast.error('Failed to delete submissions')
    }
  }

  const handleViewSubmission = (e, submission) => {
    e.stopPropagation()
    setViewingSubmission(submission)
    setViewModalOpen(true)
    // Note: Viewing no longer marks as approved - only explicit approval does
  }

  const handleSendBrochure = (e, submission) => {
    e.stopPropagation()
    if (sendingBrochureIds.includes(submission.id)) return

    showNotificationPopup({
      message: `Send the Vaart brochure to ${submission.contact_email}?`,
      cancelButton: {
        text: "Cancel",
        color: "var(--secondairy-light-medium-gray)"
      },
      actionButton: {
        text: "Send brochure",
        color: "var(--green)"
      },
      onAction: async () => {
        setSendingBrochureIds(prev => [...prev, submission.id])
        try {
          await httpClient.post(
            import.meta.env.VITE_API_URL + `/api/new-contacts/${submission.id}/send-brochure`
          )
          toast.success('Brochure sent and submission approved')
          fetchSubmissions()
        } catch (error) {
          console.error('Error sending brochure:', error)
          toast.error(error.response?.data?.error || error.response?.data?.message || 'Failed to send brochure')
        } finally {
          setSendingBrochureIds(prev => prev.filter(id => id !== submission.id))
        }
      }
    })
  }

  const closeViewModal = () => {
    setViewModalOpen(false)
    setViewingSubmission(null)
  }

  const getSubject = (submission) => {
    // Build a meaningful subject from the submission data
    const sourceTag = submission.source_tag || ''
    const source = submission.source || ''
    const formType = submission.form_type || 'contact'
    
    // Use source_tag if available, otherwise source type
    const displaySource = sourceTag || source || 'Website'
    
    if (formType === 'claim-room') {
      return `Room request${sourceTag ? ` - ${sourceTag}` : ''}`
    } else if (formType === 'brochure-request') {
      return `Brochure request${sourceTag ? ` - ${sourceTag}` : ''}`
    } else if (formType === 'contact') {
      return `Contact via ${displaySource}`
    }
    return `Submission via ${displaySource}`
  }

  const getSourceDisplay = (submission) => {
    // For the Source column - show the actual source identifier
    const sourceTag = submission.source_tag || ''
    const source = submission.source || ''
    
    // If source_tag looks like a domain, display it as-is
    // Otherwise capitalize it nicely
    if (sourceTag) {
      return sourceTag
    }
    
    // Fallback to source type with nicer formatting
    if (source === 'project-website') {
      return 'Project Website'
    } else if (source === 'vitruvi.be') {
      return 'vitruvi.be'
    }
    
    return source || 'Unknown'
  }

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A'
    const date = new Date(dateString)
    return date.toLocaleDateString('nl-BE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const formatFormValue = (value) => {
    if (value === null || value === undefined || value === '') {
      return 'N/A'
    }

    if (Array.isArray(value)) {
      const formattedValues = value
        .map((item) => formatFormValue(item))
        .filter((item) => item && item !== 'N/A')

      return formattedValues.length > 0 ? formattedValues.join(', ') : 'N/A'
    }

    if (typeof value === 'object') {
      if (typeof value.name === 'string' && value.name.trim()) {
        return value.name
      }
      if (typeof value.tag === 'string' && value.tag.trim()) {
        return value.tag
      }

      const entries = Object.entries(value).filter(([, entryValue]) => {
        return entryValue !== null && entryValue !== undefined && String(entryValue).trim() !== ''
      })

      if (entries.length === 0) {
        return 'N/A'
      }

      return entries
        .map(([entryKey, entryValue]) => `${entryKey}: ${formatFormValue(entryValue)}`)
        .join(' | ')
    }

    if (typeof value === 'boolean') {
      return value ? 'Yes' : 'No'
    }

    return String(value)
  }

  return (
    <div className='template'>
      <Header>
        <div className="header__filters" id="new-contacts__table-btns">
          {selectedSubmissions.length === 0 ? (
            <>
              <div className="search-bar" id="new-contacts__search-bar--table" onClick={handleClearSearchBar}>
                <input
                  placeholder="Search contact..."
                  value={filters.searchWord}
                  onChange={handleSearchWord}
                />
                {filters.searchWord === "" ? <SearchIcon /> : <ClearIcon />}
              </div>
              <DropDownFilter
                idList="new_contacts_filter--source"
                placeholderText="Filter on source"
                listArray={sources}
                onClickItem={handleOnClickDropdownItem}
                filters={filters}
                value={filters.sourceFilter}
                filter={"sourceFilter"}
                setValue={updateFilters}
              />
              <button
                className='refresh-filter-btn'
                title="Refresh submissions"
                onClick={fetchSubmissions}
              >
                <RefreshIcon />
              </button>
            </>
          ) : (
            <>
              <button
                className='icon-button green'
                title={`Approve ${selectedSubmissions.length} selected`}
                onClick={handleBulkApprove}
              >
                <CheckCircleOutlineIcon />
              </button>
              <button
                className='icon-button red'
                title={`Delete ${selectedSubmissions.length} selected`}
                onClick={handleBulkDelete}
              >
                <DeleteOutlineIcon />
              </button>
            </>
          )}
        </div>
      </Header>

      <MenuBar />

      <div className="template__body" id='body-new-contacts'>
        <div className={`body__section`} id='new-contacts-table'>
          <div className='table'>
            <div className='table__header-7'>
              <TableSelectCheckbox
                checked={selectedSubmissions.length === submissions.length && submissions.length > 0}
                onChange={handleSelectAll}
                isHeader={true}
              />
              <div className='table__header--field' onClick={handleSetSortFilter}>Submitted</div>
              <div className='table__header--field' onClick={handleSetSortFilter}>Email</div>
              <div className='table__header--field'>Name</div>
              <div className='table__header--field'>Subject</div>
              <div className='table__header--field'>Source</div>
              <div className='table__header--field'></div>
            </div>
            <div className='table__rows_container'>
              {loadingSubmissions ? (
                <LoadingSpinner />
              ) : submissions.length === 0 ? (
                <div className="empty-state">
                  <p>No new contact submissions found</p>
                </div>
              ) : (
                submissions.map((submission, index) => {
                  return (
                    <div
                      className='table__row-7'
                      key={index}
                      onClick={() => handleOnClickRow(submission.contact_id)}
                      data-submission-id={submission.id}
                    >
                      <TableSelectCheckbox
                        checked={selectedSubmissions.includes(submission.id)}
                        onChange={() => handleSelectSubmission(submission.id)}
                      />
                      <div className='table__row--field'>
                        {formatDate(submission.submitted_at)}
                      </div>
                      <div className='table__row--field email-cell'>
                        <span className="email-text">{submission.contact_email || "N/A"}</span>
                        {submission.is_new_contact ? (
                          <span className="new-badge" title="New contact">
                            <FiberNewIcon />
                          </span>
                        ) : (
                          <span className="existing-badge" title="Existing contact">
                            <PersonIcon />
                          </span>
                        )}
                      </div>
                      <div className='table__row--field'>
                        {submission.contact_first_name || ''} {submission.contact_last_name || ''}
                      </div>
                      <div className='table__row--field subject-cell'>
                        {getSubject(submission)}
                      </div>
                      <div className='table__row--field source-cell'>
                        <span className="source-tag">{getSourceDisplay(submission)}</span>
                        {submission.form_type && (
                          <span className="form-type">{submission.form_type}</span>
                        )}
                      </div>
                      <div className='table__row--field action-cell'>
                        <button 
                          className="approve-btn" 
                          onClick={(e) => handleSingleApprove(e, submission.id)}
                          title="Approve submission"
                        >
                          <CheckCircleOutlineIcon />
                        </button>
                        <button 
                          className="delete-btn" 
                          onClick={(e) => handleSingleDelete(e, submission)}
                          title="Delete submission"
                        >
                          <DeleteOutlineIcon />
                        </button>
                        <button 
                          className="view-btn" 
                          onClick={(e) => handleViewSubmission(e, submission)}
                          title="View submission details"
                        >
                          <VisibilityIcon />
                        </button>
                        {submission.form_type === 'brochure-request' && (
                          <button
                            className="send-brochure-btn"
                            onClick={(e) => handleSendBrochure(e, submission)}
                            disabled={sendingBrochureIds.includes(submission.id)}
                            title="Send brochure"
                          >
                            <SendIcon />
                          </button>
                        )}
                        {submission.form_type !== 'brochure-request' && (
                          <span className="send-brochure-placeholder" aria-hidden="true" />
                        )}
                      </div>
                    </div>
                  )
                })
              )}
            </div>
            <div className="pagination">
              <ArrowBackIosIcon
                className={`pagination__btn ${filters.page === 1 ? "grayed-out" : ""}`}
                onClick={handlePagination}
                data-type="previous"
                id="prevPage"
              />
              <ArrowForwardIosIcon
                className={`pagination__btn ${filters.page === totalPages ? "grayed-out" : ""}`}
                onClick={handlePagination}
                data-type="next"
                id="nextPage"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {deleteModalOpen && (
        <div className="modal-overlay" onClick={() => { setDeleteModalOpen(false); setDeletingSubmission(null); setDeleteWithContact(false); }}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>Delete {deletingSubmission ? 'Submission' : 'Submissions'}</h3>
            {deletingSubmission ? (
              <p>Are you sure you want to delete this submission from <strong>{deletingSubmission.contact_email}</strong>?</p>
            ) : (
              <p>Are you sure you want to delete {selectedSubmissions.length} submission(s)?</p>
            )}
            <div className="delete-info">
              <p><strong>What will happen:</strong></p>
              <ul>
                <li><FiberNewIcon className="inline-icon green" /> <strong>New contacts</strong> will be permanently deleted along with the submission</li>
                <li><PersonIcon className="inline-icon gray" /> <strong>Existing contacts</strong> will be kept, only the submission will be archived</li>
              </ul>
            </div>
            <div className="delete-option">
              <input
                type="checkbox"
                id="deleteWithContact"
                checked={deleteWithContact}
                onChange={(e) => setDeleteWithContact(e.target.checked)}
              />
              <label htmlFor="deleteWithContact">
                Force delete ALL associated contacts (even existing ones - use for spam cleanup)
              </label>
            </div>
            <div className="modal-actions">
              <button className="btn-cancel" onClick={() => { setDeleteModalOpen(false); setDeletingSubmission(null); setDeleteWithContact(false); }}>
                Cancel
              </button>
              <button className="btn-delete" onClick={confirmDelete}>
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* View Submission Modal */}
      {viewModalOpen && viewingSubmission && (
        <div className="modal-overlay" onClick={closeViewModal}>
          <div className="modal-content view-modal" onClick={(e) => e.stopPropagation()}>
            <div className="view-modal-header">
              <h3>{getSubject(viewingSubmission)}</h3>
              <button className="close-btn" onClick={closeViewModal}>
                <CloseIcon />
              </button>
            </div>
            <div className="view-modal-body">
              <div className="submission-info">
                <div className="info-row">
                  <span className="info-label">From:</span>
                  <span className="info-value">
                    {viewingSubmission.contact_first_name} {viewingSubmission.contact_last_name}
                    {viewingSubmission.is_new_contact ? (
                      <span className="new-contact-badge">New Contact</span>
                    ) : (
                      <span className="existing-contact-badge">Existing Contact</span>
                    )}
                  </span>
                </div>
                <div className="info-row">
                  <span className="info-label">Email:</span>
                  <span className="info-value">{viewingSubmission.contact_email}</span>
                </div>
                <div className="info-row">
                  <span className="info-label">Date:</span>
                  <span className="info-value">{formatDate(viewingSubmission.submitted_at)}</span>
                </div>
                <div className="info-row">
                  <span className="info-label">Source:</span>
                  <span className="info-value">{viewingSubmission.source_tag || viewingSubmission.source || 'N/A'}</span>
                </div>
                <div className="info-row">
                  <span className="info-label">Form Type:</span>
                  <span className="info-value">{viewingSubmission.form_type || 'contact'}</span>
                </div>
              </div>
              
              {viewingSubmission.message && (
                <div className="submission-message">
                  <h4>Message</h4>
                  <p>{viewingSubmission.message}</p>
                </div>
              )}

              {viewingSubmission.form_data && Object.keys(viewingSubmission.form_data).length > 0 && (
                <div className="submission-form-data">
                  <h4>Additional Information</h4>
                  <div className="form-data-grid">
                    {Object.entries(viewingSubmission.form_data).map(([key, value]) => (
                      <div className="form-data-item" key={key}>
                        <span className="form-data-label">{key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}:</span>
                        <span className="form-data-value">{formatFormValue(value)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <div className="view-modal-footer">
              <button 
                className="btn-primary" 
                onClick={() => {
                  closeViewModal()
                  handleOnClickRow(viewingSubmission.contact_id)
                }}
              >
                Go to Contact
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default NewContactsPage
